import React, { useState } from 'react';
import { Form, Input, Button, Typography, Alert, Grid } from 'antd';
import {
  MailOutlined, LockOutlined, MessageOutlined, WhatsAppOutlined,
  ThunderboltOutlined, TeamOutlined, BarChartOutlined, ApartmentOutlined,
  HeartOutlined,
} from '@ant-design/icons';

const { useBreakpoint } = Grid;
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const { Title, Text } = Typography;

const FEATURES = [
  { icon: <MailOutlined />,        text: 'Email Marketing Platform' },
  { icon: <MessageOutlined />,     text: 'SMS Marketing' },
  { icon: <WhatsAppOutlined />,    text: 'WhatsApp Marketing' },
  { icon: <ThunderboltOutlined />, text: 'Marketing Automation' },
  { icon: <TeamOutlined />,        text: 'Audience Management' },
  { icon: <BarChartOutlined />,    text: 'Campaign Analytics' },
  { icon: <ApartmentOutlined />,   text: 'Workflows' },
  { icon: <HeartOutlined />,       text: 'Customer Engagement' },
];

const LeftPanel = () => (
  <div style={{
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '60px',
    color: '#fff',
  }}>
    <img src="/logo.png" alt="Jay-Ho! Amplify" style={{ height: 80, objectFit: 'contain', objectPosition: 'left', marginBottom: 28 }} />
    <Text style={{ color: 'rgba(255,255,255,0.55)', fontSize: 16, display: 'block', lineHeight: 1.7, marginBottom: 36 }}>
      Increase reach and engagement with Jay-Ho! Amplify. Automate email, SMS, and WhatsApp marketing, manage audiences, create workflows, and measure campaign performance.
    </Text>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 24px' }}>
      {FEATURES.map(({ icon, text }) => (
        <div key={text} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 14, color: 'rgba(255,255,255,0.85)',
          }}>
            {icon}
          </div>
          <Text style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, fontWeight: 500 }}>{text}</Text>
        </div>
      ))}
    </div>
  </div>
);

const LoginForm = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (values) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post('/api/auth/login', values);
      localStorage.setItem('isLoggedIn', 'true');
      if (res.data.name) localStorage.setItem('userName', res.data.name);
      if (res.data.email) localStorage.setItem('userEmail', res.data.email);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid email or password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 20, borderRadius: 8 }} />}
      <Form onFinish={handleSubmit} layout="vertical" size="large">
        <Form.Item name="email" label={<span style={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>Email address</span>} rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
          <Input prefix={<MailOutlined style={{ color: '#9ca3af' }} />} placeholder="you@example.com" style={{ borderRadius: 10, height: 46 }} />
        </Form.Item>
        <Form.Item name="password" label={<span style={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>Password</span>} rules={[{ required: true, message: 'Enter your password' }]}>
          <Input.Password prefix={<LockOutlined style={{ color: '#9ca3af' }} />} placeholder="••••••••" style={{ borderRadius: 10, height: 46 }} />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0, marginTop: 8 }}>
          <Button type="primary" htmlType="submit" block loading={loading} style={{ height: 46, fontWeight: 600, borderRadius: 10, fontSize: 15 }}>
            Sign In
          </Button>
        </Form.Item>
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button
            type="button"
            onClick={onSuccess}
            style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}
          >
            Forgot password?
          </button>
        </div>
      </Form>
    </>
  );
};


const ForgotPasswordForm = ({ onBack }) => {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async ({ email }) => {
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div style={{ textAlign: 'center', paddingTop: 16 }}>
        <CheckCircleOutlined style={{ fontSize: 48, color: '#9DC533', marginBottom: 16 }} />
        <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 15, marginBottom: 24 }}>
          If that email exists, a password reset link has been sent. Check your inbox.
        </div>
        <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13 }}>
          Enter your email and we'll send you a reset link.
        </Text>
      </div>
      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 20, borderRadius: 8 }} />}
      <Form onFinish={handleSubmit} layout="vertical" size="large">
        <Form.Item name="email" label={<span style={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>Email address</span>} rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
          <Input prefix={<MailOutlined style={{ color: '#9ca3af' }} />} placeholder="you@example.com" style={{ borderRadius: 10, height: 46 }} />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" block loading={loading} style={{ height: 46, fontWeight: 600, borderRadius: 10, fontSize: 15 }}>
            Send Reset Link
          </Button>
        </Form.Item>
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' }}>
            Back to Sign In
          </button>
        </div>
      </Form>
    </>
  );
};

const Login = () => {
  const [showForgot, setShowForgot] = useState(false);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      background: '#000',
    }}>
      {!isMobile && <LeftPanel />}

      <div style={{
        width: isMobile ? '100%' : 480,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '32px 24px' : '40px',
        background: isMobile ? 'transparent' : 'rgba(255,255,255,0.04)',
        borderLeft: isMobile ? 'none' : '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{ width: '100%', maxWidth: 380 }}>
          {/* Mobile logo */}
          {isMobile && (
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <img src="/logo.png" alt="Jay-Ho! Amplify" style={{ height: 48, objectFit: 'contain' }} />
            </div>
          )}

          {showForgot ? (
            <>
              <div style={{ marginBottom: 28 }}>
                <Title level={3} style={{ color: '#fff', margin: 0 }}>Reset your password</Title>
              </div>
              <ForgotPasswordForm onBack={() => setShowForgot(false)} />
            </>
          ) : (
            <>
              <div style={{ marginBottom: 28 }}>
                <Title level={3} style={{ color: '#fff', margin: 0 }}>Sign in</Title>
                <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>Welcome back — enter your credentials to continue</Text>
              </div>
              <LoginForm onSuccess={() => setShowForgot(true)} />
            </>
          )}
          <div style={{ marginTop: 32, textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>
            Need help? <a href="mailto:Jayho@jay-ho.com" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}>Jayho@jay-ho.com</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
